// src/api/auth.js
//
// Auth API surface used by the login/onboarding pages and logout buttons.
// All requests go through the global axios interceptors set up in ./api.js,
// so the silent-refresh and 401-retry behaviour is automatic.

import axios from 'axios';
import {
    API_URL,
    setAccessToken,
    hardLogout,
    refreshAccessToken,
    scheduleProactiveRefresh,
    getAccessToken,
    secondsUntilExpiry,
} from './api';

const API_AUTH_URL = `${API_URL}/api/auth`;

// Login. Returns the full response so existing callers ({ res.data.token, ... })
// keep working. We *also* persist the access token + schedule the proactive
// refresh internally so callers don't need to remember.
export const login = async (credentials) => {
    const res = await axios.post(`${API_AUTH_URL}/login`, credentials, {
        withCredentials: true,
    });
    if (res?.data?.token) {
        setAccessToken(res.data.token);
        scheduleProactiveRefresh(res.data.token);
    }
    return res;
};

export const register = (userData) =>
    axios.post(`${API_AUTH_URL}/register`, userData);

// Manually trigger a refresh — useful for "is my session still alive?" checks.
export const refreshSession = () => refreshAccessToken();

// Server-side logout (revokes refresh cookie/family) + local cleanup +
// redirect to /login. Components that own a logout button should call this.
export const logout = () => hardLogout();

// "Log out everywhere" — admin tool. Revokes every refresh row for the user.
export const logoutEverywhere = async () => {
    try {
        await axios.post(
            `${API_AUTH_URL}/logout-all`,
            {},
            { withCredentials: true }
        );
    } catch {}
    return hardLogout({ silent: true });
};

export {
    setAccessToken,
    getAccessToken,
    secondsUntilExpiry,
};
